import React, { useMemo } from "react";
import { c, jt, t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { ConfiguratorProductPrice } from "../../../common/ConfiguratorProductPrice";
import { Flair } from "../../../common/Flair";
import { Image } from "../../../common/Image";
import { Link } from "../../../common/Link";
import RatingGraphic from "../../../common/RatingGraphic";
import { RichText } from "../../../common/RichText";
import { InternalProduct } from "../../../models/catalogue";
import type { IStringAttribute } from "../../../models/catalogue.interfaces";
import { GroupedTileModelsType } from "../../../templates/ShopTileTemplate";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import format from "../../../utils/format";
import { useCurrentBreakpoint } from "../../../utils/hooks";
import { sortProductOptions } from "../../../utils/sorting";
import { DiscountDisplayMode } from "../../configurator/constants";
import { ATTRIBUTE_CLASSES, CompareTileType } from "../ShopCompareTypes";
import { AttributeList } from "../elements/AttributeList";
import { ModelSelector } from "../elements/ModelSelector";

import styles from "./index.module.scss";

interface ICompareTileProps {
    tile: CompareTileType;
    handleSelectProduct?: (newTile: number) => void;
    selectedProducts: number[];
    selectedSize: string;
    groupedDropdownModels: GroupedTileModelsType;
}

export const CompareTile = (props: ICompareTileProps) => {
    const currentBreakpoint = useCurrentBreakpoint();
    const tile = props.tile;

    // Find the correct variant based on current product, size, and a default feel
    const variantUrl = useMemo(() => {
        if (!tile.children || tile.children.length === 0) {
            return tile.link;
        }

        const selectedFeel =
            tile.feel_options && tile.feel_options.length > 0
                ? tile.feel_options[0]
                : ATTRIBUTE_CLASSES.medium;

        // Find matching child variant
        const matchingChild = tile.children.find((child: InternalProduct) => {
            // Attributes is an array of {code, value} objects, need to find by code
            const sizeAttr = Array.isArray(child.attributes)
                ? child.attributes.find(
                      (attr: IStringAttribute) => attr.code === "option_size",
                  )
                : child.attributes.option_size;
            const feelAttr = Array.isArray(child.attributes)
                ? child.attributes.find(
                      (attr: IStringAttribute) => attr.code === "option_feel",
                  )
                : child.attributes.option_feel;

            const sizeValue = sizeAttr?.value;
            const feelValue = feelAttr?.value;

            const hasSize = sizeValue === props.selectedSize;
            const hasFeel = feelValue === selectedFeel;
            return hasSize && hasFeel;
        });

        return matchingChild?.link || tile.link;
    }, [tile.children, tile.feel_options, props.selectedSize, tile.link]);

    const sizePriceMap = tile?.size_price_map;
    const isSelectedSizeAvailable =
        props.selectedSize &&
        sizePriceMap &&
        props.selectedSize in sizePriceMap;
    const effectivePrice = isSelectedSizeAvailable
        ? sizePriceMap[props.selectedSize]
        : tile.price;
    const totalPrice = getProductPrice(effectivePrice, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    // Following variables are necessary for i18n of the financing string
    const formattedTotalPrice = format.money(totalPrice);
    const financingMonthsLength =
        effectivePrice.per_month_term_length?.toString();
    const financingMonths = effectivePrice.per_month_term_length ? (
        <span key="financing-months">
            {t`${financingMonthsLength} months`}
            <sup>1</sup>
        </span>
    ) : null;
    const financingMonthsSuperscript = (
        <sup key="financing-months-supscript-2">2</sup>
    );

    const sortedFeelOptions = sortProductOptions(
        "option_feel",
        tile.feel_options,
    );
    const attributeList = sortedFeelOptions.map((feelOption: string) => ({
        title: feelOption,
        class:
            (
                Object.keys(ATTRIBUTE_CLASSES) as Array<
                    keyof typeof ATTRIBUTE_CLASSES
                >
            ).find((key) => ATTRIBUTE_CLASSES[key] === feelOption) || "",
    }));

    const onModelSelectChange = (productId: number) => {
        props.handleSelectProduct?.(productId);
    };

    return (
        <div className={styles.container} key={JSON.stringify(tile.id)}>
            {tile.primary_flair && (
                <div className={styles.primaryFlair}>
                    <Flair flair={tile.primary_flair} />
                </div>
            )}
            <div className={styles.contentContainer}>
                <div className={styles.imageContainer}>
                    {tile.product_image?.url && (
                        <Image
                            alt={tile.product_image.title || ""}
                            src={tile.product_image.url}
                        />
                    )}
                    {tile.secondary_flair && (
                        <div className={styles.secondaryFlair}>
                            <Flair flair={tile.secondary_flair} />
                        </div>
                    )}
                </div>
                <ModelSelector
                    id={`compare_tile_model_${tile.product}`}
                    label={tile.title || ""}
                    value={tile.product || null}
                    onChange={onModelSelectChange}
                    groupedModels={props.groupedDropdownModels}
                    selectedProducts={props.selectedProducts}
                />
                <a
                    href={`${tile.product}#reviews_body`}
                    className={styles.starRating}
                >
                    <RatingGraphic
                        cardClass={styles.starRating}
                        cardSize="large"
                        numReviews={tile.num_reviews}
                        rating={tile.rating}
                        reviewNumberClass={styles.reviewLink}
                    />
                </a>
                {tile.description && (
                    <RichText
                        html={tile.description}
                        className={styles.description}
                    />
                )}
                <AttributeList list={attributeList} />
            </div>
            <div
                className={`${
                    currentBreakpoint.belowLaptop
                        ? styles.priceContainer
                        : styles.priceContainerHorizontal
                }`}
            >
                <div>
                    {isSelectedSizeAvailable ? (
                        <>
                            <ConfiguratorProductPrice
                                price={effectivePrice}
                                strikeThroughMSRP={false}
                                discountDisplayMode={
                                    DiscountDisplayMode.DISABLED
                                }
                                actualPriceStyle={"shop"}
                            />
                            {effectivePrice.per_month_term_length && (
                                <div
                                    className={`${
                                        currentBreakpoint.belowLaptop
                                            ? styles.financingPlanContainer
                                            : styles.financingPlanContainerHorizontal
                                    }`}
                                >
                                    <span>
                                        {jt`or ${effectivePrice.per_month}/mo${financingMonthsSuperscript} for ${financingMonths}`}
                                    </span>
                                    <br />
                                    <span>
                                        {c("compare-financing-payments")
                                            .t`total payments of ${formattedTotalPrice}`}
                                    </span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div>{t`Unavailable in this size`}</div>
                    )}
                </div>
                <div className={styles.shopNowButton}>
                    {isSelectedSizeAvailable ? (
                        <Link
                            href={variantUrl}
                            className="button al-comparisongrid-shopnow"
                        >
                            {t`Shop Now`}
                            <span className="ada-screenreader-only">
                                {interpolate(t`for %(productName)s`, {
                                    productName: tile.title || "",
                                })}
                            </span>
                        </Link>
                    ) : (
                        <button
                            className="button al-comparisongrid-shopnow"
                            disabled
                        >
                            {t`Unavailable`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
